import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthHeader from '../components/AuthHeader';
import axios from 'axios';
import SweetAlert from '../components/SweetAlert';
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import '../assets/styles/common.css';
import eyeIcon from '../assets/images/eye.png';
import eyeCloseIcon from '../assets/images/eyeClose.png';
const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1
    companyName: '',
    registrationNumber: '',
    taxNumber: '',
    billingAddress: '',
    city: '',
    postalCode: '',
    agencyType: '',
    country: '',
    // Step 2
    contactPerson: '',
    email: '',
    phone: '',
    jobTitle: '',
    website: '',
    password: '',
    confirmPassword: '',
    additionalNotes: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    // Company Name - Advanced Validation
    const companyName = formData.companyName.trim();
    if (!companyName) {
      newErrors.companyName = "Company name is required";
    } else if (companyName.length < 3) {
      newErrors.companyName = "Company name must be at least 3 characters";
    } else if (companyName.length > 100) {
      newErrors.companyName = "Company name is too long (maximum 100 characters)";
    } else if (/^(.)\1{5,}$/.test(companyName)) {   // Detects repeated characters like aaaaaa, 111111
      newErrors.companyName = "Company name cannot contain repeated characters";
    } else if (!/[a-zA-Z]/.test(companyName)) {     // Must contain at least one letter
      newErrors.companyName = "Company name must contain at least one letter";
    }

    // Registration Number
    if (!formData.registrationNumber.trim()) {
      newErrors.registrationNumber = "Registration number is required";
    } else if (formData.registrationNumber.trim().length < 5) {
      newErrors.registrationNumber = "Registration number must be at least 5 characters";
    }

    // Tax Number
    if (!formData.taxNumber.trim()) {
      newErrors.taxNumber = "Tax number is required";
    }

    // Billing Address
    if (!formData.billingAddress.trim()) {
      newErrors.billingAddress = "Billing address is required";
    } else if (formData.billingAddress.trim().length < 10) {
      newErrors.billingAddress = "Please enter a complete address";
    }

    // City
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    } else if (formData.city.trim().length < 2) {
      newErrors.city = "City name is too short";
    }

    // Postal Code
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = "Postal code is required";
    } else if (!/^[A-Z0-9\s-]{3,10}$/i.test(formData.postalCode.trim())) {
      newErrors.postalCode = "Please enter a valid postal code";
    }

    // Agency Type
    if (!formData.agencyType) {
      newErrors.agencyType = "Please select an agency type";
    }

    // Country
    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    } else if (formData.country.trim().length < 2) {
      newErrors.country = "Please enter a valid country name";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.contactPerson.trim()) newErrors.contactPerson = "Contact person name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email address";
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      }
    } else if (step === 2) {
      if (validateStep2()) {
        setStep(3);
      }
    }
  };

  const prevStep = () => setStep(step - 1);

  const handleFinalSubmit = () => {
    setLoading(true);
    // Your API call here
    axios.post(`${process.env.REACT_APP_API_URL}/api/website/register-agency`, formData)
      .then(response => {
        setLoading(false);
        if (response.data.status === 1) {
          //alert("✅ Registration Successful!");
          SweetAlert.success("Registration Successful!", "Your application has been submitted successfully.");
          navigate('/');
        } else {
          //alert(response.data.message || "Submission failed");
          SweetAlert.error(response.data.message || "Submission failed");
        }
      })
      .catch(error => {
        setLoading(false);
        //alert(error.response.data.message || "Server error. Please try again.");
        SweetAlert.error(error.response.data.message || "Server error. Please try again.");
      });
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">

          <AuthHeader subtitle="Agency Registration" />

          <h1 className="text-center mb-2">Register Your Agency</h1>
          <p className="text-center text-muted mb-5">Complete all steps to submit your partner application.</p>


          {/* Stepper with Checkmark */}
          <div className="d-flex justify-content-center mb-5">
            <div className="d-flex align-items-center">
              <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 1 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '40px', height: '40px' }}>
                {step > 1 ? '✓' : '1'}
              </div>
              <span className="mx-3 fw-bold">Company Info</span>

              <div className={`border-bottom flex-grow-1 mx-3 ${step >= 2 ? 'border-primary' : 'border-secondary'}`} style={{ height: '2px', width: '80px' }}></div>

              <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 2 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '40px', height: '40px' }}>
                {step > 2 ? '✓' : '2'}
              </div>
              <span className="mx-3 fw-bold">Contact Details</span>

              <div className={`border-bottom flex-grow-1 mx-3 ${step >= 3 ? 'border-primary' : 'border-secondary'}`} style={{ height: '2px', width: '80px' }}></div>

              <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 3 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '40px', height: '40px' }}>
                3
              </div>
              <span className="mx-3 fw-bold">Review & Submit</span>
            </div>
          </div>

          {/* Step 1 */}
          {step === 1 && (
            <div className="card shadow">
              <div className="card-body p-5">
                <h4 className="mb-4">Company Information</h4>

                <div className="row g-4">
                  <div className="col-12">
                    <label className="form-label">Company Name <span className="text-danger">*</span></label>
                    <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} className="form-control" />
                    {errors.companyName && <small className="text-danger">{errors.companyName}</small>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Company Registration Number <span className="text-danger">*</span></label>
                    <input type="text" name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} className="form-control" />
                    {errors.registrationNumber && <small className="text-danger">{errors.registrationNumber}</small>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Social Security / Tax Number <span className="text-danger">*</span></label>
                    <input type="text" name="taxNumber" value={formData.taxNumber} onChange={handleChange} className="form-control" />
                    {errors.taxNumber && <small className="text-danger">{errors.taxNumber}</small>}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Billing Address <span className="text-danger">*</span></label>
                    <input type="text" name="billingAddress" value={formData.billingAddress} onChange={handleChange} className="form-control" />
                    {errors.billingAddress && <small className="text-danger">{errors.billingAddress}</small>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">City <span className="text-danger">*</span></label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} className="form-control" />
                    {errors.city && <small className="text-danger">{errors.city}</small>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Postal Code <span className="text-danger">*</span></label>
                    <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} className="form-control" />
                    {errors.postalCode && <small className="text-danger">{errors.postalCode}</small>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Country <span className="text-danger">*</span></label>
                    <input type="text" name="country" value={formData.country} onChange={handleChange} className="form-control" />
                    {errors.country && <small className="text-danger">{errors.country}</small>}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Agency Type <span className="text-danger">*</span></label>
                    <select name="agencyType" value={formData.agencyType} onChange={handleChange} className="form-select">
                      <option value="">Select type...</option>
                      <option value="hotel">Hotel</option>
                      <option value="resort">Resort</option>
                    </select>
                    {errors.agencyType && <small className="text-danger">{errors.agencyType}</small>}
                  </div>
                </div>

                <div className="d-flex gap-3 mt-5">
                  <button className="btn btn-secondary px-4" onClick={() => navigate('/')}>Cancel</button>
                  <button className="btn btn-primary px-5" onClick={nextStep}>Next Step →</button>
                </div>
              </div>
            </div>
          )}

          {/* ==================== STEP 2 - Contact + Login ==================== */}
          {step === 2 && (
            <div className="card shadow-sm">
              <div className="card-body p-5">
                <h4>👤 Contact Information</h4>

                <div className="row g-4">
                  <div className="col-12">
                    <label>Contact Person Full Name <span className="text-danger">*</span></label>
                    <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="form-control" />
                  </div>

                  <div className="col-md-6">
                    <label>Email Address <span className="text-danger">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control" />
                    {errors.email && <small className="text-danger">{errors.email}</small>}
                  </div>

                  <div className="col-md-6">
                    <label>
                      Phone Number <span className="text-danger">*</span>
                    </label>

                    <PhoneInput
                      international
                      defaultCountry="IS"
                      countries={["IS"]}
                      addInternationalOption={false}
                      value={formData.phone}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          phone: value || "",
                        }))
                      }
                    />

                    {errors.phone && (
                      <small className="text-danger">{errors.phone}</small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label>Job Title</label>
                    <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} className="form-control" />
                  </div>

                  <div className="col-md-6">
                    <label>Website</label>
                    <input type="url" name="website" value={formData.website} onChange={handleChange} className="form-control" />
                  </div>

                  <div className="col-md-6">
                    <label>Password <span className="text-danger">*</span></label>
                    <div className="password-container">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="form-control"
                      />
                      <img
                        src={showPassword ? eyeCloseIcon : eyeIcon}
                        alt={showPassword ? 'Hide password' : 'Show password'}
                        className="password-toggle"
                        onClick={() => setShowPassword(prev => !prev)}
                      />
                    </div>
                    {errors.password && <small className="text-danger">{errors.password}</small>}
                  </div>

                  <div className="col-md-6">
                    <label>Confirm Password <span className="text-danger">*</span></label>
                    <div className="password-container">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="form-control"
                      />
                      <img
                        src={showConfirmPassword ? eyeCloseIcon : eyeIcon}
                        alt={showConfirmPassword ? 'Hide password' : 'Show password'}
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(prev => !prev)}
                      />
                    </div>
                    {errors.confirmPassword && <small className="text-danger">{errors.confirmPassword}</small>}
                  </div>

                  <div className="col-12">
                    <label>Additional Notes</label>
                    <textarea name="additionalNotes" value={formData.additionalNotes} onChange={handleChange} className="form-control" rows="3" />
                  </div>
                </div>

                <div className="d-flex gap-3 mt-5">
                  <button className="btn btn-secondary" onClick={prevStep}>← Back</button>
                  <button className="btn btn-primary" onClick={nextStep}>Next Step →</button>
                </div>
              </div>
            </div>
          )}

          {/* ==================== STEP 3: REVIEW & SUBMIT ==================== */}
          {step === 3 && (
            <div className="card shadow-sm">
              <div className="card-body p-5">
                <h4 className="mb-4">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Review & Submit
                </h4>
                {/* Login Details */}
                <div className="review-section mb-4">
                  <h6 className="text-uppercase text-muted mb-3">LOGIN DETAILS</h6>
                  <div className="bg-light p-4 rounded-3">
                    <p className="mb-1"><strong>{formData.email || 'N/A'}</strong></p>
                    <p className="mb-1">{formData.password}</p>
                  </div>
                </div>

                {/* Company Section */}
                <div className="review-section mb-4">
                  <h6 className="text-uppercase text-muted mb-3">COMPANY</h6>
                  <div className="bg-light p-4 rounded-3">
                    <p className="mb-1"><strong>{formData.companyName || 'N/A'}</strong></p>
                    <p className="mb-1">{formData.registrationNumber}</p>
                    <p className="mb-1">{formData.taxNumber}</p>
                    <p className="mb-0">{formData.billingAddress}, {formData.city} {formData.postalCode}, {formData.country}</p>
                  </div>
                </div>

                {/* Contact Section */}
                <div className="review-section mb-4">
                  <h6 className="text-uppercase text-muted mb-3">CONTACT</h6>
                  <div className="bg-light p-4 rounded-3">
                    <p className="mb-1"><strong>{formData.contactPerson || 'N/A'}</strong></p>
                    <p className="mb-1">{formData.email}</p>
                    <p className="mb-0">{formData.phone}</p>
                  </div>
                </div>

                {/* Pending Notice */}
                <div className="alert alert-warning border-0 d-flex align-items-start" style={{ backgroundColor: '#fff3cd' }}>
                  <i className="bi bi-info-circle me-3 mt-1"></i>
                  <div>
                    Your account will remain <strong>pending</strong> until an administrator reviews and approves your application.
                  </div>
                </div>

                <div className="d-flex gap-3 mt-5">
                  <button className="btn btn-outline-secondary px-4" onClick={prevStep}>
                    ← Back
                  </button>
                  <button
                    className="btn btn-primary px-5 flex-grow-1"
                    onClick={handleFinalSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;